import { getConfig } from '@newfront-insurance/shared-public-config';
import { DefaultChatTransport } from 'ai';
import type { ChatTransport } from 'ai';

import type { UIMessageWithMetadata } from './types';

interface CreateTransportOptions {
  apiEndpoint?: string;
  getToken: () => Promise<string | undefined>;
  getUserUuid: () => string;
  getAccountUuid?: () => string | undefined;
}

/**
 * Creates a chat transport configured for Nova's API.
 * Handles authentication, request body preparation, and error handling.
 */
export function createNovaTransport({
  apiEndpoint,
  getToken,
  getUserUuid,
  getAccountUuid,
}: CreateTransportOptions): ChatTransport<UIMessageWithMetadata> {
  const config = getConfig();
  const baseApiUrl = `${config.API.AI}${apiEndpoint || '/api/ai/nova/v1/chat/completions'}`;

  return new DefaultChatTransport({
    api: baseApiUrl,
    fetch: async (url, options) => {
      const token = await getToken();

      return fetch(url, {
        ...options,
        headers: {
          ...options?.headers,
          authorization: `Bearer ${token}`,
        },
      });
    },
    prepareReconnectToStreamRequest: ({ id }) => {
      return {
        api: `${config.API.AI}/api/ai/nova/v1/chat/completions/${id}/stream`,
      };
    },
    prepareSendMessagesRequest: (request) => {
      const lastMessage = request.messages.at(-1);

      if (!lastMessage) {
        throw new Error('No last message found');
      }

      // Call functions to get current values (avoid stale closures)
      const userUuid = getUserUuid();
      const accountUuid = getAccountUuid?.() || request.body?.accountUuid;
      const { additionalContext, ...restBodyData } = request.body || {};

      return {
        body: {
          userUuid,
          accountUuid,
          chatUuid: request.id.replace('new-chat-', ''),
          messageUuid: request.body?.messageUuid,
          role: lastMessage.role,
          parts: lastMessage.parts,
          ...additionalContext,
          ...restBodyData,
        },
      };
    },
  });
}
